import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { useState, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, Link as LinkIcon, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

// Ensure lists render correctly inside the editor (Tailwind base reset strips list-style)
const editorStyles = `
.rte-editor ul { list-style-type: disc; padding-left: 1.5em; margin: 0.25em 0; }
.rte-editor ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.25em 0; }
.rte-editor li { margin: 0; }
.rte-editor strong { font-weight: 700; }
.rte-editor em { font-style: italic; }
.rte-editor a { color: #2563eb; text-decoration: underline; }
.rte-editor p { margin: 0 0 0.25em 0; }
.rte-editor p:last-child { margin-bottom: 0; }
`;

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
}

const ToolbarButton = ({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onMouseDown={(e) => {
      e.preventDefault(); // prevent editor losing focus
      onClick();
    }}
    className={cn(
      'flex items-center px-2 py-1 rounded text-gray-600 hover:bg-gray-200 transition-colors',
      active && 'bg-gray-200 text-gray-900 font-medium',
      disabled && 'opacity-40 cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  hasError,
  className,
}: RichTextEditorProps) => {
  const { t } = useTranslation()
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  // Force re-render whenever the editor state changes (selection, marks, etc.)
  const [, forceUpdate] = useState(0);

  // Inject editor styles once
  useEffect(() => {
    const id = 'rte-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = editorStyles;
      document.head.appendChild(style);
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
    ],
    content: value || '',
    onTransaction: () => forceUpdate(n => n + 1),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // treat empty editor as empty string
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'rte-editor min-h-[100px] focus:outline-none p-3',
      },
    },
  });

  // Sync external value into editor (e.g. when modal opens with existing data)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (current !== incoming && !(current === '<p></p>' && incoming === '')) {
      editor.commands.setContent(incoming);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().unsetLink().run();
    } else {
      const resolved = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      editor.chain().focus().setLink({ href: resolved }).run();
    }
    setLinkUrl('');
    setShowLinkInput(false);
  }, [editor, linkUrl]);

  const openLinkInput = useCallback(() => {
    if (!editor) return;
    const existing = editor.getAttributes('link').href as string | undefined;
    setLinkUrl(existing ?? '');
    setShowLinkInput(true);
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'border rounded-md bg-white overflow-hidden',
        hasError ? 'border-red-500' : 'border-gray-200',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0',
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-gray-100 px-2 py-1.5 bg-gray-50 flex-wrap">
        <ToolbarButton
          title={t('editor.bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <Bold className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.boldLabel')}</span>
        </ToolbarButton>

        <ToolbarButton
          title={t('editor.italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <Italic className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.italicLabel')}</span>
        </ToolbarButton>

        <ToolbarButton
          title={t('editor.underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.underlineLabel')}</span>
        </ToolbarButton>

        <ToolbarButton
          title={t('editor.strikethrough')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
        >
          <Strikethrough className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.strikeLabel')}</span>
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton
          title={t('editor.bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <List className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.bulletsLabel')}</span>
        </ToolbarButton>

        <ToolbarButton
          title={t('editor.numberedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <ListOrdered className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.numberedLabel')}</span>
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton
          title={t('editor.insertLink')}
          onClick={openLinkInput}
          active={editor.isActive('link')}
          disabled={editor.state.selection.empty && !editor.isActive('link')}
        >
          <LinkIcon className="h-3.5 w-3.5" />
          <span className="text-sm ml-1">{t('editor.linkLabel')}</span>
        </ToolbarButton>

        {editor.isActive('link') && (
          <ToolbarButton
            title={t('editor.removeLink')}
            onClick={() => editor.chain().focus().unsetLink().run()}
          >
            <Unlink className="h-3.5 w-3.5" />
          </ToolbarButton>
        )}
      </div>

      {/* Link input row */}
      {showLinkInput && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 bg-gray-50">
          <Input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') { setShowLinkInput(false); }
            }}
            placeholder="https://..."
            className="h-7 text-sm"
          />
          <Button type="button" size="sm" className="h-7 text-sm px-2" onClick={applyLink}>
            OK
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-sm px-2"
            onClick={() => setShowLinkInput(false)}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Editor area */}
      <div className="relative">
        {editor.isEmpty && placeholder && (
          <p className="pointer-events-none absolute top-3 left-3 text-gray-400 select-none">
            {placeholder}
          </p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
